import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { 
  BarChart3, 
  TrendingUp,
  Search,
  Link2,
  Shield,
  CheckCircle2,
  Loader2,
  Sparkles
} from 'lucide-react';
import { Card, CardContent } from '../../../components/ui/card';
import { Progress } from '../../../components/ui/progress';
import { Button } from '../../../components/ui/button';
import { api } from '../../../context/AuthContext';

const METRICS = [
  { id: 'traffic', label: 'Organic Traffic Baseline', icon: TrendingUp },
  { id: 'keywords', label: 'Current Keyword Rankings', icon: Search },
  { id: 'backlinks', label: 'Backlink Profile Snapshot', icon: Link2 },
  { id: 'technical', label: 'Technical SEO Audit', icon: Shield },
];

export default function BaselineCapture({ onComplete }) {
  const [progress, setProgress] = useState(0);
  const [currentMetric, setCurrentMetric] = useState(0);
  const [completed, setCompleted] = useState([]);
  const [isComplete, setIsComplete] = useState(false);
  const [baseline, setBaseline] = useState(null);
  const hasStarted = useRef(false);

  useEffect(() => {
    if (hasStarted.current) return;
    hasStarted.current = true;

    const captureBaseline = async () => {
      // Step 1: Organic Traffic (0-25%)
      const progressInterval = setInterval(() => {
        setProgress(p => Math.min(p + 1, 25));
      }, 80);

      // Actually call the backend to complete onboarding and capture baseline
      let result = null;
      try {
        const res = await api.post('/onboarding/complete');
        result = res.data?.baseline || null;
      } catch (e) {
        console.error('Baseline capture error:', e);
      }

      clearInterval(progressInterval);
      setProgress(25);
      setCompleted(['traffic']);
      setCurrentMetric(1);
      setBaseline(result);

      // Step 2: Keywords (25-50%)
      await new Promise(r => setTimeout(r, 800));
      setProgress(50);
      setCompleted(prev => [...prev, 'keywords']);
      setCurrentMetric(2);

      // Step 3: Backlinks (50-75%)
      await new Promise(r => setTimeout(r, 800));
      setProgress(75);
      setCompleted(prev => [...prev, 'backlinks']);
      setCurrentMetric(3);

      // Step 4: Technical (75-100%)
      await new Promise(r => setTimeout(r, 800));
      setProgress(100);
      setCompleted(prev => [...prev, 'technical']);
      setIsComplete(true);
    };

    captureBaseline();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-indigo-50 flex items-center justify-center p-6">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-xl"
      >
        <Card className="gds-card">
          <CardContent className="p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-2xl bg-indigo-100 flex items-center justify-center mx-auto mb-4">
                {isComplete ? (
                  <CheckCircle2 className="w-8 h-8 text-emerald-600" />
                ) : (
                  <BarChart3 className="w-8 h-8 text-indigo-600" />
                )}
              </div>
              <h1 className="text-2xl font-bold font-heading mb-2">
                {isComplete ? 'Baseline Captured!' : 'Setting Your Performance Baseline'}
              </h1>
              <p className="text-slate-600">
                {isComplete 
                  ? 'Your baseline metrics have been recorded. We\'ll use this to measure your growth!' 
                  : 'We\'re pulling your current metrics from Google Analytics & Search Console'
                }
              </p>
            </div>

            {!isComplete && (
              <div className="mb-8">
                <div className="flex justify-between text-sm text-slate-600 mb-2">
                  <span>Fetching real performance data...</span>
                  <span className="font-medium">{progress}%</span>
                </div>
                <Progress value={progress} className="h-3" />
              </div>
            )}

            <div className="grid grid-cols-2 gap-4 mb-8">
              {METRICS.map((metric, index) => {
                const isCapturing = currentMetric === index && !completed.includes(metric.id);
                const isDone = completed.includes(metric.id);
                
                return (
                  <motion.div
                    key={metric.id}
                    initial={{ opacity: 0.5 }}
                    animate={{ opacity: isDone || isCapturing ? 1 : 0.5 }}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      isDone 
                        ? 'border-emerald-200 bg-emerald-50' 
                        : isCapturing
                          ? 'border-indigo-200 bg-indigo-50'
                          : 'border-slate-200 bg-slate-50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                        isDone 
                          ? 'bg-emerald-100 text-emerald-600'
                          : isCapturing
                            ? 'bg-indigo-100 text-indigo-600'
                            : 'bg-slate-100 text-slate-400'
                      }`}>
                        {isDone ? (
                          <CheckCircle2 className="w-4 h-4" />
                        ) : isCapturing ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <metric.icon className="w-4 h-4" />
                        )}
                      </div>
                      <span className={`text-sm font-medium ${
                        isDone ? 'text-emerald-700' : isCapturing ? 'text-indigo-700' : 'text-slate-500'
                      }`}>
                        {metric.label}
                      </span>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {/* Show real baseline values when done */}
            {isComplete && baseline && baseline.source !== 'pending_integration' && (
              <div className="grid grid-cols-2 gap-3 mb-6">
                <div className="p-3 rounded-lg bg-slate-50 text-center">
                  <p className="text-xl font-bold font-heading">
                    {baseline.organic_traffic?.toLocaleString() ?? '—'}
                  </p>
                  <p className="text-xs text-slate-500">Organic Traffic</p>
                </div>
                <div className="p-3 rounded-lg bg-slate-50 text-center">
                  <p className="text-xl font-bold font-heading">
                    {baseline.keyword_count ?? '—'}
                  </p>
                  <p className="text-xs text-slate-500">Keywords Tracked</p>
                </div>
                <div className="p-3 rounded-lg bg-slate-50 text-center">
                  <p className="text-xl font-bold font-heading">
                    {baseline.keywords_in_top_10 ?? '—'}
                  </p>
                  <p className="text-xs text-slate-500">Top 10 Rankings</p>
                </div>
                <div className="p-3 rounded-lg bg-slate-50 text-center">
                  <p className="text-xl font-bold font-heading">
                    {baseline.total_impressions?.toLocaleString() ?? '—'}
                  </p>
                  <p className="text-xs text-slate-500">Impressions</p>
                </div>
              </div>
            )}

            {isComplete && baseline?.source === 'pending_integration' && (
              <div className="p-4 rounded-xl bg-amber-50 border border-amber-200 mb-6">
                <p className="text-sm font-medium text-amber-900">Baseline metrics pending</p>
                <p className="text-xs text-amber-700 mt-1">
                  Connect Google Analytics 4 and Search Console to capture your live traffic and keyword data. Your baseline will be recorded once the integrations are active.
                </p>
              </div>
            )}

            <div className="p-4 rounded-xl bg-blue-50 border border-blue-200">
              <div className="flex items-start gap-3">
                <Sparkles className="w-5 h-5 text-blue-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-blue-900">Why this matters</p>
                  <p className="text-xs text-blue-700 mt-1">
                    This baseline proves the impact of our work over your 12-month partnership. Every improvement is tracked against these starting metrics.
                  </p>
                </div>
              </div>
            </div>

            {!isComplete && (
              <p className="text-center text-sm text-slate-500 mt-6">
                Pulling data from connected services...
              </p>
            )}

            {isComplete && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="mt-8"
              >
                <Button 
                  onClick={onComplete}
                  className="w-full gds-btn-primary py-6 text-lg"
                >
                  <Sparkles className="w-5 h-5 mr-2" />
                  Go to Dashboard
                </Button>
              </motion.div>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
